import { useState, useEffect, useCallback, useRef } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'
import {
    AuditSnapshotKind,
    AuditSnapshotRow,
    ProjectSnapshotDetail,
    PropertySnapshotDetail,
} from '../containers/PropertyAudit/auditMetrics'
import {
    MOCK_PROJECT_SNAPSHOTS,
    MOCK_PROPERTY_SNAPSHOTS,
} from '../containers/PropertyAudit/mockAuditSnapshots'

interface GenerateAuditResponse {
    task_id: string
}

export interface AuditGenerateProgress {
    current: number
    total: number
    message?: string
}

interface AuditGenerateStatusResponse {
    status: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILED'
    report_id?: number
    error?: string
    current?: number
    total?: number
    message?: string
}

export interface AuditSnapshotApiRow {
    id: number | string
    label: string
    created_at: string
    is_scheduled: boolean
    xlsx_url?: string | null
    has_detail?: boolean
    metrics: Record<string, number | null>
}

interface AuditSnapshotsResponse {
    results: AuditSnapshotApiRow[]
}

interface UseAuditSnapshotsOptions {
    kind: AuditSnapshotKind
    /** Active EZFolio organization; scopes rollup + generate to that org. */
    organizationId?: number | null
    /** When non-empty, roll up only these apartments; empty = all in org scope. */
    apartmentIds?: number[]
    useMock?: boolean
    onGenerateSuccess?: () => void | Promise<void>
}

const mapApiRows = (rows: AuditSnapshotApiRow[]): AuditSnapshotRow[] =>
    rows.map((row) => ({
        id: row.id,
        label: row.label,
        created_at: row.created_at,
        is_scheduled: row.is_scheduled,
        xlsx_url: row.xlsx_url,
        has_detail: row.has_detail,
        metrics: row.metrics,
    }))

export const useAuditSnapshots = (options: UseAuditSnapshotsOptions) => {
    const {
        kind,
        organizationId = null,
        apartmentIds = [],
        useMock = false,
        onGenerateSuccess,
    } = options
    const [isLoading, setIsLoading] = useState(false)
    const [rows, setRows] = useState<AuditSnapshotRow[]>([])
    const [activeTaskId, setActiveTaskId] = useState<string | null>(null)
    const [isGeneratingProject, setIsGeneratingProject] = useState(false)

    const onGenerateSuccessRef = useRef(onGenerateSuccess)
    onGenerateSuccessRef.current = onGenerateSuccess
    const organizationIdRef = useRef(organizationId)
    organizationIdRef.current = organizationId

    const apartmentIdsKey = apartmentIds
        .slice()
        .sort((a, b) => a - b)
        .join(',')

    const getMockRows = useCallback(() => {
        return kind === 'property_audit'
            ? MOCK_PROPERTY_SNAPSHOTS
            : MOCK_PROJECT_SNAPSHOTS
    }, [kind])

    const fetchSnapshots = useCallback(async () => {
        setIsLoading(true)
        try {
            if (useMock) {
                setRows(getMockRows())
                setIsLoading(false)
                return
            }

            const params: {
                kind: AuditSnapshotKind
                apartment_ids?: string
                organization_id?: number
            } = {
                kind,
            }
            if (apartmentIdsKey) {
                params.apartment_ids = apartmentIdsKey
            }
            if (organizationId != null) {
                params.organization_id = organizationId
            }

            const res = await axiosInstance.get<AuditSnapshotsResponse>(
                'stats/reports/audit-snapshots/',
                {
                    params,
                    validateStatus: (status) => status >= 200 && status < 300,
                },
            )
            setRows(mapApiRows(res.data.results ?? []))
            setIsLoading(false)
        } catch (e: any) {
            // Fall back to mocks if the endpoint is not available yet
            if (e?.response?.status === 404) {
                setRows(getMockRows())
                setIsLoading(false)
                return
            }
            setIsLoading(false)
            toast.error(
                e.response?.data?.message ||
                    e.response?.data?.error ||
                    'Error loading audit snapshots',
            )
            return Promise.reject(e)
        }
    }, [kind, apartmentIdsKey, organizationId, useMock, getMockRows])

    useEffect(() => {
        void fetchSnapshots()
    }, [fetchSnapshots])

    const terminalToastShownRef = useRef<string | null>(null)

    const {
        mutateAsync: requestPropertyAudit,
        isLoading: isCreatingAudit,
    } = useMutation<GenerateAuditResponse, Error, void>({
        mutationFn: async () => {
            const orgId = organizationIdRef.current
            const body =
                orgId != null
                    ? {
                          filters: {
                              organization_id: orgId,
                              include_all_workspaces: true,
                          },
                      }
                    : undefined
            const res = await axiosInstance.post<GenerateAuditResponse>(
                'stats/reports/generate-audit-report/',
                body,
                { validateStatus: (status) => status >= 200 && status < 300 },
            )
            if (!res.data?.task_id) {
                throw new Error('No task_id returned from server')
            }
            return res.data
        },
        onSuccess: (data) => {
            terminalToastShownRef.current = null
            setActiveTaskId(data.task_id)
        },
        onError: (e: any) => {
            toast.error(
                e.response?.data?.message ||
                    e.response?.data?.error ||
                    e.message ||
                    'Error requesting property audit',
            )
        },
    })

    const requestProjectReport = useCallback(async () => {
        setIsGeneratingProject(true)
        try {
            const response = await axiosInstance.get(
                '/workorder/org-project-analytics/generate-report/',
                {
                    params: { archived: false },
                    validateStatus: (status) => status >= 200 && status < 300,
                },
            )
            if (response.status === 200) {
                toast.success('Project report generation started')
                // Project generate is fire-and-forget today; refresh shortly
                setTimeout(() => {
                    void onGenerateSuccessRef.current?.()
                    void fetchSnapshots()
                }, 5000)
            } else {
                toast.error('Project report generation failed')
            }
        } catch (e: any) {
            toast.error(
                e.response?.data?.message ||
                    e.response?.data?.error ||
                    'Project report generation failed',
            )
        } finally {
            setIsGeneratingProject(false)
        }
    }, [fetchSnapshots])

    const { data: generateStatus } = useQuery<AuditGenerateStatusResponse>({
        queryKey: ['property-audit-generate-status', activeTaskId],
        queryFn: async () => {
            if (!activeTaskId) {
                throw new Error('No task_id available')
            }
            const res = await axiosInstance.get<AuditGenerateStatusResponse>(
                'stats/reports/generate-audit-report-status/',
                {
                    params: { task_id: activeTaskId },
                    validateStatus: (status) => status >= 200 && status < 300,
                },
            )
            return res.data
        },
        enabled: !!activeTaskId,
        refetchInterval: (query) => {
            if (!query) return false
            const latestData = (query as any).state?.data as
                | AuditGenerateStatusResponse
                | undefined
            if (
                latestData?.status === 'SUCCESS' ||
                latestData?.status === 'FAILED'
            ) {
                return false
            }
            return 3000
        },
        retry: false,
    })

    useEffect(() => {
        if (
            generateStatus?.status === 'SUCCESS' &&
            activeTaskId &&
            terminalToastShownRef.current !== activeTaskId
        ) {
            terminalToastShownRef.current = activeTaskId
            setActiveTaskId(null)
            toast.success('Property audit generated successfully!')
            void onGenerateSuccessRef.current?.()
            void fetchSnapshots()
        }
    }, [generateStatus?.status, activeTaskId, fetchSnapshots])

    useEffect(() => {
        if (
            generateStatus?.status === 'FAILED' &&
            activeTaskId &&
            terminalToastShownRef.current !== activeTaskId
        ) {
            terminalToastShownRef.current = activeTaskId
            setActiveTaskId(null)
            toast.error(
                generateStatus.error || 'Failed to generate property audit',
            )
        }
    }, [generateStatus?.status, generateStatus?.error, activeTaskId])

    const fetchProjectSnapshotDetail = useCallback(
        async (rowId: string | number): Promise<ProjectSnapshotDetail> => {
            const params: {
                kind: AuditSnapshotKind
                row_id: string
                apartment_ids?: string
                organization_id?: number
            } = {
                kind: 'project_analytics',
                row_id: String(rowId),
            }
            if (apartmentIdsKey) {
                params.apartment_ids = apartmentIdsKey
            }
            if (organizationId != null) {
                params.organization_id = organizationId
            }
            const res = await axiosInstance.get<ProjectSnapshotDetail>(
                'stats/reports/audit-snapshot-detail/',
                {
                    params,
                    validateStatus: (status) => status >= 200 && status < 300,
                },
            )
            return res.data
        },
        [apartmentIdsKey, organizationId],
    )

    const fetchPropertySnapshotDetail = useCallback(
        async (rowId: string | number): Promise<PropertySnapshotDetail> => {
            const params: {
                kind: AuditSnapshotKind
                row_id: string
                apartment_ids?: string
                organization_id?: number
            } = {
                kind: 'property_audit',
                row_id: String(rowId),
            }
            if (apartmentIdsKey) {
                params.apartment_ids = apartmentIdsKey
            }
            if (organizationId != null) {
                params.organization_id = organizationId
            }
            const res = await axiosInstance.get<PropertySnapshotDetail>(
                'stats/reports/audit-snapshot-detail/',
                {
                    params,
                    validateStatus: (status) => status >= 200 && status < 300,
                },
            )
            return res.data
        },
        [apartmentIdsKey, organizationId],
    )

    const isGeneratingProperty = isCreatingAudit || !!activeTaskId

    const generateProgress: AuditGenerateProgress | null =
        generateStatus?.status === 'PROGRESS'
            ? {
                  current: generateStatus.current ?? 0,
                  total: generateStatus.total ?? 1,
                  message: generateStatus.message,
              }
            : null

    return {
        isLoading,
        rows,
        fetchSnapshots,
        fetchProjectSnapshotDetail,
        fetchPropertySnapshotDetail,
        isGeneratingProperty,
        isGeneratingProject,
        generateProgress,
        requestPropertyAudit,
        requestProjectReport,
    }
}
